import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, BellOff, Plus, Trash2, Eye, Download, Crown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { usePremium } from "@/lib/premium";
import { getBilling } from "@/lib/billing";
import { isAnalyticsOptedOut, setAnalyticsOptOut } from "@/lib/analytics";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  loadPrefs,
  savePrefs,
  permissionStatus,
  requestPermission,
  showSystemNudge,
  pickNudge,
  recordShown,
  FREQUENCY_OPTIONS,
  DEFAULT_NUDGES,
} from "@/lib/nudges";
import {
  rescheduleNudges,
  requestNativeNotificationPermission,
  isNative,
} from "@/lib/native";
import { SETTINGS } from "@/constants/testIds";

export default function Settings() {
  const [prefs, setPrefs] = useState(() => loadPrefs());
  const [perm, setPerm] = useState(() => permissionStatus());
  const [draft, setDraft] = useState("");

  useEffect(() => {
    // refresh permission status in case the user changed it in the browser
    setPerm(permissionStatus());
  }, []);

  const update = (patch) => {
    const next = savePrefs(patch);
    setPrefs(next);
    // Reschedule native nudge bank whenever prefs change. No-op on web.
    rescheduleNudges().catch(() => undefined);
  };

  const handleEnable = async () => {
    if (perm === "default" || perm === "unsupported") {
      // Native shell uses its own permission API; web uses the browser API.
      const result = isNative()
        ? await requestNativeNotificationPermission()
        : await requestPermission();
      setPerm(result);
      // Still allow in-app card nudges even if permission is declined.
      update({ enabled: true });
    } else {
      update({ enabled: !prefs.enabled });
    }
  };

  const handleAddPhrase = () => {
    const text = draft.trim();
    if (!text) return;
    if (prefs.library.includes(text)) {
      setDraft("");
      return;
    }
    update({ library: [...prefs.library, text] });
    setDraft("");
  };

  const handleRemovePhrase = (phrase) => {
    update({ library: prefs.library.filter((p) => p !== phrase) });
  };

  const handleResetLibrary = () => {
    update({ library: DEFAULT_NUDGES.slice() });
    toast("Library reset to the originals.");
  };

  const handlePreview = async () => {
    const text = pickNudge(prefs);
    const shown = await showSystemNudge(text);
    if (!shown) {
      toast(text, { duration: 6000 });
    }
    update({ ...recordShown(prefs) });
  };

  const permLabel = {
    default: "Not asked yet",
    granted: "Allowed",
    denied: "Blocked by your browser",
    unsupported: "Not available in this browser",
  }[perm];

  return (
    <PageContainer testid={SETTINGS.root}>
      <PageHeader
        title="Settings"
        subtitle="How — and how often — UnAnonymous gets to whisper."
        variant="exit"
        exitTo="/home"
        exitLabel="Exit"
        testid={SETTINGS.exit}
      />

      {/* Nudges section */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl p-5 mb-6"
        style={{ background: "var(--un-bg-2)" }}
      >
        <div className="flex items-start gap-3 mb-1">
          {prefs.enabled ? (
            <Bell size={18} style={{ color: "var(--un-plum)" }} />
          ) : (
            <BellOff size={18} style={{ color: "var(--un-text-3)" }} />
          )}
          <div>
            <h2
              className="text-[18px] font-medium"
              style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
            >
              Gentle nudges
            </h2>
            <p
              className="text-[13px] mt-0.5 leading-relaxed"
              style={{ color: "var(--un-text-3)" }}
            >
              A soft phrase, on a quiet schedule. Default off. Easy to silence.
            </p>
          </div>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-between mt-5">
          <div>
            <p
              className="text-[14px] font-medium"
              style={{ color: "var(--un-text-2)", fontFamily: "Manrope, sans-serif" }}
            >
              {prefs.enabled ? "On" : "Off"}
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--un-text-3)" }}>
              {permLabel}
            </p>
          </div>
          <button
            type="button"
            data-testid={SETTINGS.toggle}
            onClick={handleEnable}
            className="relative w-14 h-8 rounded-full un-press transition-colors"
            style={{
              background: prefs.enabled ? "var(--un-plum)" : "rgba(74,32,64,0.18)",
            }}
            role="switch"
            aria-checked={prefs.enabled}
            aria-label="Gentle nudges"
          >
            <span
              className="absolute top-1 w-6 h-6 rounded-full transition-all"
              style={{
                background: "#FDFBF7",
                left: prefs.enabled ? "1.625rem" : "0.25rem",
                boxShadow: "0 2px 6px rgba(74,32,64,0.25)",
              }}
            />
          </button>
        </div>

        {/* Frequency */}
        {prefs.enabled && (
          <div className="mt-6">
            <p
              className="text-[11px] uppercase tracking-[0.2em] mb-2"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              How often
            </p>
            <div className="flex flex-wrap gap-2">
              {FREQUENCY_OPTIONS.map((opt) => {
                const active = prefs.frequencyId === opt.id;
                return (
                  <button
                    key={opt.id}
                    data-testid={`${SETTINGS.freqPrefix}${opt.id}`}
                    onClick={() => update({ frequencyId: opt.id })}
                    className="px-3.5 py-1.5 rounded-full text-xs font-medium un-press transition-all"
                    style={{
                      background: active ? "var(--un-plum)" : "rgba(74,32,64,0.05)",
                      color: active ? "#FDFBF7" : "var(--un-text-2)",
                      fontFamily: "Manrope, sans-serif",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Library */}
        {prefs.enabled && (
          <div className="mt-6">
            <p
              className="text-[11px] uppercase tracking-[0.2em] mb-3"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              The library
            </p>
            <div className="flex flex-col gap-2 mb-3">
              {prefs.library.map((phrase) => (
                <div
                  key={phrase}
                  data-testid={`${SETTINGS.libraryItemPrefix}${encodeURIComponent(phrase)}`}
                  className="flex items-start gap-3 rounded-2xl p-3"
                  style={{ background: "#FDFBF7" }}
                >
                  <p
                    className="flex-1 text-[14px] italic leading-snug"
                    style={{
                      color: "var(--un-text-1)",
                      fontFamily: "Cormorant Garamond, serif",
                      fontSize: "16px",
                    }}
                  >
                    “{phrase}”
                  </p>
                  <button
                    type="button"
                    aria-label="remove phrase"
                    onClick={() => handleRemovePhrase(phrase)}
                    className="un-press p-1"
                  >
                    <Trash2 size={14} style={{ color: "var(--un-text-3)" }} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                data-testid={SETTINGS.libraryInput}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddPhrase();
                  }
                }}
                placeholder="Add your own phrase…"
                className="flex-1 px-3 py-2 rounded-full text-[13px] focus:outline-none"
                style={{
                  background: "#FDFBF7",
                  border: "1px solid rgba(74,32,64,0.10)",
                  color: "var(--un-text-1)",
                  fontFamily: "DM Sans, sans-serif",
                }}
              />
              <SoftButton
                data-testid={SETTINGS.libraryAdd}
                variant="secondary"
                size="sm"
                iconLeft={<Plus size={14} />}
                onClick={handleAddPhrase}
                disabled={!draft.trim()}
              >
                Add
              </SoftButton>
            </div>

            <button
              type="button"
              onClick={handleResetLibrary}
              className="mt-3 text-[11px] underline underline-offset-2"
              style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
            >
              reset to the originals
            </button>
          </div>
        )}

        {/* Preview */}
        {prefs.enabled && (
          <div className="mt-6">
            <SoftButton
              data-testid={SETTINGS.preview}
              variant="secondary"
              size="md"
              iconLeft={<Eye size={14} />}
              onClick={handlePreview}
            >
              Send me one now (preview)
            </SoftButton>
          </div>
        )}

        <p
          className="mt-6 text-[11px] leading-relaxed"
          style={{ color: "var(--un-text-3)" }}
        >
          Nudges live entirely on your device. No one is listening. Once you
          install UnAnonymous to your home screen, they can arrive even when
          the app is closed.
        </p>
      </motion.section>

      {/* Backup & export */}
      <Link
        to="/backup"
        data-testid="settings-backup-link"
        className="block rounded-3xl p-5 mb-6 un-press"
        style={{ background: "var(--un-bg-2)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="shrink-0 rounded-2xl flex items-center justify-center"
            style={{ width: 40, height: 40, background: "rgba(91,57,84,0.08)" }}
          >
            <Download size={16} style={{ color: "var(--un-plum)" }} />
          </div>
          <div className="flex-1">
            <p
              className="text-[15px] font-medium"
              style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
            >
              Backup & export
            </p>
            <p className="text-[12px]" style={{ color: "var(--un-text-3)" }}>
              Download your letters · keep a copy · restore from backup
            </p>
          </div>
        </div>
      </Link>

      {/* Premium */}
      <PremiumSection />

      {/* Privacy controls */}
      <div className="rounded-3xl p-5 mb-6" style={{ background: "var(--un-bg-2)" }}>
        <p className="text-[15px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>Privacy choices</p>
        <p className="text-[12px] leading-relaxed mt-1" style={{ color: "var(--un-text-3)" }}>Review essential storage and optional analytics. Private reflection content is never used for advertising.</p>
        <button onClick={() => window.dispatchEvent(new Event("un-open-privacy"))} className="mt-3 px-4 py-2.5 rounded-full text-[12px] font-medium un-press" style={{ background: "rgba(91,57,84,.07)", color: "var(--un-plum)" }}>Manage privacy</button>
      </div>

      {/* Optional product analytics */}
      <AnalyticsSection />
    </PageContainer>
  );
}

function PremiumSection() {
  const { premium, refresh } = usePremium();
  const [busy, setBusy] = useState(false);

  const handleRestore = async () => {
    setBusy(true);
    const res = await getBilling().restore();
    setBusy(false);
    if (res.ok) {
      await refresh();
      toast(res.premium ? "Premium restored." : "No previous Premium purchase found.");
    } else {
      toast(res.message || "Restore is available in the Android app.");
    }
  };

  return (
    <div
      data-testid="settings-premium-section"
      className="rounded-3xl p-5 mb-6"
      style={{ background: "var(--un-bg-2)" }}
    >
      <div className="flex items-center gap-3 mb-2">
        <Crown size={16} style={{ color: "#C4A16A" }} />
        <p className="text-[15px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
          UnAnonymous Premium
        </p>
      </div>
      <p className="text-[12px] leading-relaxed mb-3" style={{ color: "var(--un-text-3)" }}>
        {premium
          ? "Active. Recurring subscriptions can be managed in Google Play → Subscriptions."
          : "The full reset library, unlimited Toolkit, and more ways to reflect. Core support and crisis resources remain free."}
      </p>
      <div className="flex items-center gap-3">
        {!premium && (
          <Link
            to="/premium?source=settings"
            data-testid="settings-premium-link"
            className="px-4 py-2 rounded-full text-[13px] font-medium un-press"
            style={{ background: "var(--un-plum)", color: "#FDFBF7", fontFamily: "Manrope, sans-serif" }}
          >
            Learn more
          </Link>
        )}
        <button
          data-testid="settings-restore-btn"
          onClick={handleRestore}
          disabled={busy}
          className="text-[12px] underline underline-offset-2 un-press"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Restore purchases
        </button>
      </div>
    </div>
  );
}

function AnalyticsSection() {
  const [optedOut, setOptedOut] = useState(() => isAnalyticsOptedOut());

  const toggle = () => {
    const next = !optedOut;
    setAnalyticsOptOut(next);
    setOptedOut(next);
    toast(next ? "Optional product analytics turned off." : "Optional product analytics turned on.");
  };

  return (
    <div
      data-testid="settings-analytics-section"
      className="rounded-3xl p-5 mb-6"
      style={{ background: "var(--un-bg-2)" }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <p className="text-[15px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
            Optional product analytics
          </p>
          <p className="text-[12px] leading-relaxed mt-1" style={{ color: "var(--un-text-3)" }}>
            A random installation ID and simple product events (like "reset completed") can help us improve the app. Analytics does not send your reflections, journal entries, letters, or other private writing.
          </p>
        </div>
        <button
          data-testid="settings-analytics-toggle"
          onClick={toggle}
          role="switch"
          aria-checked={!optedOut}
          aria-label="Optional product analytics"
          className="shrink-0 w-12 h-7 rounded-full un-press relative transition-colors"
          style={{ background: optedOut ? "rgba(91,57,84,0.15)" : "var(--un-plum)" }}
        >
          <span
            className="absolute top-1 w-5 h-5 rounded-full transition-all"
            style={{ background: "#FDFBF7", left: optedOut ? 4 : 24 }}
          />
        </button>
      </div>
    </div>
  );
}
